import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Building,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Crosshair,
  Database,
  FileText,
  FolderOpen,
  Headphones,
  Layers,
  Lock,
  Server,
  Shield,
  ShieldAlert,
  Target,
  Users,
  Zap,
} from 'lucide-react';
import { motion } from 'motion/react';
import PropTypes from 'prop-types';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';

const coreProducts = [
  {
    id: 'grc',
    name: 'GRC',
    title: 'Governance, Risk & Compliance',
    description:
      'Unified platform for managing governance frameworks, risk assessments, and regulatory compliance across your organization.',
    icon: Shield,
    color: 'from-purple-500 to-purple-600',
    badgeColor: 'from-purple-100 to-purple-50 text-purple-800',
    badge: 'Core Module',
    badgeIcon: CheckCircle,
    features: [
      'Risk Assessment',
      'Policy Management',
      'Audit Workflows',
      'Regulatory Mapping',
    ],
    href: '/platform/grc',
  },
  {
    id: 'iam',
    name: 'IAM',
    title: 'Identity & Access Management',
    description:
      'Streamline identity lifecycle management with automated provisioning, intelligent access reviews, and compliance reporting.',
    icon: Users,
    color: 'from-amber-500 to-amber-600',
    badgeColor: 'from-amber-100 to-amber-50 text-amber-800',
    badge: 'Identity',
    badgeIcon: Lock,
    features: [
      'Automated Provisioning',
      'Access Reviews',
      'Role Management',
      'Compliance Reports',
    ],
    href: '/platform/iam',
  },
  {
    id: 'cmdb',
    name: 'CMDB',
    title: 'Configuration Management Database',
    description:
      'Centralize and manage all your IT assets, configurations, and dependencies with intelligent discovery and automated mapping.',
    icon: Database,
    color: 'from-blue-500 to-blue-600',
    badgeColor: 'from-blue-100 to-blue-50 text-blue-800',
    badge: 'Assets',
    badgeIcon: Server,
    features: [
      'Asset Discovery',
      'Dependency Mapping',
      'Change Tracking',
      'Integration APIs',
    ],
    href: '/platform/cmdb',
  },
  {
    id: 'xdr',
    name: 'XDR',
    title: 'Extended Detection & Response',
    description:
      'Comprehensive security monitoring with real-time threat detection, MITRE ATT&CK mapping, and automated incident response.',
    icon: ShieldAlert,
    color: 'from-red-500 to-red-600',
    badgeColor: 'from-red-100 to-red-50 text-red-800',
    badge: 'Detection',
    badgeIcon: AlertTriangle,
    features: [
      'Threat Detection',
      'MITRE ATT&CK',
      'Incident Response',
      'Security Analytics',
    ],
    href: '/platform/xdr',
  },
];

const extendedProducts = [
  {
    id: 'service-desk',
    name: 'Service Desk',
    title: 'IT Service Management',
    description:
      'Streamline IT operations with integrated service desk, ticket management, and automated workflows for faster resolution.',
    icon: Headphones,
    color: 'from-green-500 to-green-600',
    badgeColor: 'from-green-100 to-green-50 text-green-800',
    badge: 'Support',
    badgeIcon: Headphones,
    features: [
      'Ticket Management',
      'SLA Tracking',
      'Knowledge Base',
      'Self-Service Portal',
    ],
    href: '/platform/service-desk',
  },
  {
    id: 'pentest',
    name: 'Penetration Testing',
    title: 'Penetration Testing & Assessment',
    description:
      'Comprehensive security testing platform for vulnerability assessments, penetration testing, and security validation.',
    icon: Crosshair,
    color: 'from-rose-500 to-rose-600',
    badgeColor: 'from-rose-100 to-rose-50 text-rose-800',
    badge: 'Testing',
    badgeIcon: Target,
    features: [
      'Vulnerability Scanning',
      'Penetration Testing',
      'Security Reports',
      'Remediation Tracking',
    ],
    href: '/platform/pentest',
  },
  {
    id: 'documents',
    name: 'Documents',
    title: 'AI-Powered Document Management',
    description:
      'Intelligent document management with AI-powered analysis, version control, and compliance-ready document workflows.',
    icon: FolderOpen,
    color: 'from-orange-500 to-orange-600',
    badgeColor: 'from-orange-100 to-orange-50 text-orange-800',
    badge: 'Documents',
    badgeIcon: FileText,
    features: [
      'AI Document Analysis',
      'Version Control',
      'Policy Templates',
      'Audit Trail',
    ],
    href: '/platform/documents',
  },
  {
    id: 'business',
    name: 'Business',
    title: 'Business Entity & Process Management',
    description:
      'Manage organizational structure, business processes, and entity relationships for complete operational visibility.',
    icon: Building,
    color: 'from-indigo-500 to-indigo-600',
    badgeColor: 'from-indigo-100 to-indigo-50 text-indigo-800',
    badge: 'Operations',
    badgeIcon: Briefcase,
    features: [
      'Org Structure',
      'Process Mapping',
      'Entity Management',
      'Relationship Tracking',
    ],
    href: '/platform/business',
  },
  {
    id: 'resilience',
    name: 'BCRP',
    title: 'Business Continuity & Resiliency Planning',
    description:
      'Plan, test, and execute business continuity and disaster recovery with RTO/RPO tracking and crisis management.',
    icon: Shield,
    color: 'from-teal-500 to-teal-600',
    badgeColor: 'from-teal-100 to-teal-50 text-teal-800',
    badge: 'Continuity',
    badgeIcon: Shield,
    features: [
      'BCP Planning',
      'Disaster Recovery',
      'RTO/RPO Tracking',
      'Crisis Management',
    ],
    href: '/platform/resilience',
  },
  {
    id: 'data-governance',
    name: 'Data Governance',
    title: 'Data Governance & Privacy Management',
    description:
      'Comprehensive data governance with cataloging, classification, lineage tracking, and transmission monitoring for compliance and privacy.',
    icon: Database,
    color: 'from-violet-500 to-violet-600',
    badgeColor: 'from-violet-100 to-violet-50 text-violet-800',
    badge: 'Data',
    badgeIcon: Lock,
    features: [
      'Data Catalog',
      'Classification',
      'Lineage Tracking',
      'Transmission Monitoring',
    ],
    href: '/platform/grc',
  },
];

// Reusable Product Card Component
function ProductCard({ product, index, onNavigateToTop }) {
  const IconComponent = product.icon;
  return (
    <motion.div
      className="group"
      viewport={{ once: true }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      <Card className="h-full border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-500 overflow-hidden relative">
        <div
          className={`absolute inset-0 bg-gradient-to-br ${product.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none`}
        />

        <motion.div
          whileHover={{ y: -5 }}
          className="relative h-full"
          transition={{ duration: 0.3 }}
        >
          <CardHeader>
            <div className="flex items-start justify-between mb-4">
              <motion.div
                whileHover={{ rotate: 10 }}
                className={`w-14 h-14 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
              >
                <IconComponent className="w-7 h-7 text-white" />
              </motion.div>

              <div className={`flex items-center bg-gradient-to-r ${product.badgeColor} px-2.5 py-1 rounded-full text-xs`}>
                <product.badgeIcon className="w-3 h-3 mr-1" />
                {product.badge}
              </div>
            </div>

            <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">
              {product.title}
            </CardTitle>

            <CardDescription className="text-gray-600 text-sm leading-relaxed">
              {product.description}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              {product.features.map((feature, idx) => (
                <div
                  key={idx}
                  className="flex items-center text-xs text-gray-600 bg-gray-50 rounded-full px-2.5 py-1"
                >
                  <Zap className="w-3 h-3 text-blue-500 mr-1 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <Button
              className="w-full bg-white border border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-all duration-300 text-sm"
              onClick={() => onNavigateToTop(product.href)}
            >
              Learn More
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </CardContent>
        </motion.div>
      </Card>
    </motion.div>
  );
}

ProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onNavigateToTop: PropTypes.func.isRequired,
};

const AUTO_SCROLL_INTERVAL = 2000; // 2 seconds for testing

// Extended Capabilities Carousel with auto-scroll
function ExtendedCapabilitiesCarousel({ products, onNavigateToTop }) {
  const carouselRef = useRef(null);
  const containerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const scrollNext = useCallback(() => {
    const container = carouselRef.current;
    if (!container) {
      return;
    }

    const cardWidth = 340;
    const maxScroll = container.scrollWidth - container.clientWidth;

    if (container.scrollLeft >= maxScroll - 10) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
    }
    else {
      container.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  }, []);

  const scrollPrev = useCallback(() => {
    const container = carouselRef.current;
    if (!container) {
      return;
    }
    container.scrollBy({ left: -340, behavior: 'smooth' });
  }, []);

  // Visibility observer - start auto-scroll when visible
  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Auto-scroll effect
  useEffect(() => {
    if (isPaused || !isVisible) {
      return;
    }

    const interval = setInterval(() => {
      scrollNext();
    }, AUTO_SCROLL_INTERVAL);

    return () => clearInterval(interval);
  }, [isPaused, isVisible, scrollNext]);

  return (
    <div
      ref={containerRef}
      className="mt-12"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center">
          <Zap className="w-5 h-5 mr-2 text-blue-600" />
          Extended Capabilities
        </h3>

        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Previous"
            className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 hover:border-gray-400 transition-all"
            onClick={scrollPrev}
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>

          <button
            type="button"
            aria-label="Next"
            className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 hover:border-gray-400 transition-all"
            onClick={scrollNext}
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Carousel Container */}
      <div className="relative">
        <div
          ref={carouselRef}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4 items-stretch"
        >
          {products.map((product) => {
            const IconComponent = product.icon;
            const BadgeIconComponent = product.badgeIcon;
            return (
              <div
                key={product.id}
                className="flex-shrink-0 flex"
                style={{ width: 'calc(25% - 18px)', minWidth: '300px' }}
              >
                <Card className="flex-1 border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-500 overflow-hidden relative group flex flex-col">
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${product.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none`}
                  />

                  <CardHeader className="flex-grow-0">
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className={`w-14 h-14 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                      >
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>

                      <div className={`flex items-center bg-gradient-to-r ${product.badgeColor} px-2.5 py-1 rounded-full text-xs`}>
                        <BadgeIconComponent className="w-3 h-3 mr-1" />
                        {product.badge}
                      </div>
                    </div>

                    <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">
                      {product.title}
                    </CardTitle>

                    <CardDescription className="text-gray-600 text-sm leading-relaxed">
                      {product.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex flex-col flex-grow justify-end">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {product.features.map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-center text-xs text-gray-600 bg-gray-50 rounded-full px-2.5 py-1"
                        >
                          <Zap className="w-3 h-3 text-blue-500 mr-1 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      className="w-full bg-white border border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-all duration-300 text-sm relative z-10"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onNavigateToTop(product.href);
                      }}
                    >
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

ExtendedCapabilitiesCarousel.propTypes = {
  products: PropTypes.array.isRequired,
  onNavigateToTop: PropTypes.func.isRequired,
};

function PlatformOverview({ onNavigateToTop }) {
  return (
    <section id="platform-overview" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          viewport={{ once: true }}
          className="text-center mb-16"
          transition={{ duration: 0.8 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Layers className="w-4 h-4 mr-2" />
            Integrated Platform
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Discover the BOSSS Platform
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Nine integrated modules — built to work together for complete
            cybersecurity oversight.
          </p>
        </motion.div>

        {/* Core Platform Section */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0 }}
          viewport={{ once: true }}
          whileInView={{ opacity: 1 }}
        >
          <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <Shield className="w-5 h-5 mr-2 text-blue-600" />
            Core Platform
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreProducts.map((product, index) => (
              <ProductCard
                index={index}
                key={product.id}
                product={product}
                onNavigateToTop={onNavigateToTop}
              />
            ))}
          </div>
        </motion.div>

        {/* Extended Capabilities Section - Carousel */}
        <ExtendedCapabilitiesCarousel
          products={extendedProducts}
          onNavigateToTop={onNavigateToTop}
        />

        {/* Bottom CTA */}
        <motion.div
          viewport={{ once: true }}
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-12 border border-blue-100 shadow-lg max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Zap className="w-4 h-4 mr-2" />
              See BOSSS in Action
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to unify your cybersecurity operations?
            </h3>

            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Experience how BOSSS can transform your security posture with
              seamless integration across all modules. Schedule a personalized
              demo today.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/demo')}
              >
                Schedule Platform Demo
              </Button>

              <Button
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/contact')}
              >
                Contact Us
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

PlatformOverview.propTypes = {
  onNavigate: PropTypes.func.isRequired,
  onNavigateToTop: PropTypes.func.isRequired,
};

export default PlatformOverview;
